/**
 * Configuracoes — router-shell do hub unificado.
 *
 * /configuracoes → redireciona para a primeira aba (perfil).
 * Cada aba é uma sub-rota real. As abas pessoais/dados/aparência (Fase C) já
 * têm conteúdo real; os 4 módulos movidos (Importações, Integrações,
 * Integrações CAD, Acessos) entram na Fase D — por ora placeholders fiéis,
 * com as rotas antigas ainda ativas.
 */
import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import ConfiguracoesShell from "./Shell";
import { TABS, abaPermitida } from "./tabs";

import PerfilTab from "./tabs/Perfil";
import SegurancaTab from "./tabs/Seguranca";
import NotificacoesTab from "./tabs/Notificacoes";
import IntegracaoDinaBoxTab from "./tabs/IntegracaoDinaBox";
import IntegracoesIATab from "./tabs/IntegracoesIA";
import CalendarioCelularTab from "./tabs/CalendarioCelular";
import AparenciaTab from "./tabs/Aparencia";
import PlanilhasTab from "./tabs/Planilhas";
import ExportacaoTab from "./tabs/Exportacao";
import CalendarioTab from "./tabs/Calendario";
import MinhaEmpresaTab from "./tabs/MinhaEmpresa";
import AgendaTab from "./tabs/Agenda";
import GuiaConectar from "./conexoes/GuiaConectar";

// Fase D — módulos existentes movidos para sub-rotas (reaproveitados como estão).
import Importacoes from "../Administracao/Importacoes";
import IntegracoesCAD from "../Administracao/IntegracoesCAD";
import MarketplaceIntegracoes from "../Integracoes";
import Acessos from "../Acessos";

// Abas com conteúdo real. As de Sistema/Dados (importacoes, integracoes,
// integracoes-cad, acessos) embutem os módulos completos já existentes.
const COMPONENTES = {
  perfil: PerfilTab,
  seguranca: SegurancaTab,
  notificacoes: NotificacoesTab,
  dinabox: IntegracaoDinaBoxTab,
  conectar: GuiaConectar,
  "integracoes-ia": IntegracoesIATab,
  "calendario-celular": CalendarioCelularTab,
  aparencia: AparenciaTab,
  planilhas: PlanilhasTab,
  exportacao: ExportacaoTab,
  calendario: CalendarioTab,
  empresa: MinhaEmpresaTab,
  agenda: AgendaTab,
  importacoes: Importacoes,
  integracoes: MarketplaceIntegracoes,
  "integracoes-cad": IntegracoesCAD,
  acessos: Acessos,
};

/** Guard de rota: bloqueia acesso direto por URL a abas sem permissão. */
function TabGuard({ gate, children }) {
  const { isMaster, hasAllPermissions, hasAnyPermission, temModulo } = useAuth();
  const ok = abaPermitida(gate, { isMaster, hasAllPermissions, hasAnyPermission, temModulo });
  if (!ok) {
    return (
      <section className="set-section">
        <div className="set-section__body">
          <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
            Você não tem permissão para acessar esta seção.
          </div>
        </div>
      </section>
    );
  }
  return children;
}

/** Placeholder fiel (cabeçalho da seção) — abas ainda não migradas (Fase D). */
function TabPlaceholder({ titulo, desc }) {
  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">{titulo}</h2>
        <p className="set-section__desc">{desc}</p>
      </div>
      <div className="set-section__body">
        <div style={{ padding: "28px 4px", textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
          Em construção — o conteúdo desta aba chega na próxima fase.
        </div>
      </div>
    </section>
  );
}

export default function Configuracoes() {
  return (
    <Routes>
      <Route element={<ConfiguracoesShell />}>
        <Route index element={<Navigate to="perfil" replace />} />
        {TABS.map((t) => {
          const Comp = COMPONENTES[t.path];
          const element = (
            <TabGuard gate={t.gate}>
              {Comp ? <Comp /> : <TabPlaceholder titulo={t.titulo} desc={t.desc} />}
            </TabGuard>
          );
          return (
            <Route key={t.path} path={t.splat ? `${t.path}/*` : t.path} element={element} />
          );
        })}
        {/* A antiga "Identidade da unidade" virou parte de Minha empresa. */}
        <Route path="unidade" element={<Navigate to="../empresa" replace />} />
        <Route path="*" element={<Navigate to="perfil" replace />} />
      </Route>
    </Routes>
  );
}
